import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, Eraser, FileSignature, ShieldCheck } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function SignaturePad({ onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={170} data-testid="offer-signature-pad"
        className="w-full h-44 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} data-testid="offer-signature-clear" className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

export default function OfferSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [subErr, setSubErr] = useState("");

  useEffect(() => {
    fetch(`${BACKEND}/api/esign/offer/${token}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setData(d); setName(d.name || ""); setDone(d.status === "signed"); })
      .catch(() => setErr("签署链接无效或已失效 Link invalid or expired"));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setSubErr("");
    try {
      const res = await fetch(`${BACKEND}/api/esign/offer/${token}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signature: sig, signedName: name }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "");
      setDone(true);
    } catch (e) { setSubErr(e.message || "提交失败，请重试"); }
    setSubmitting(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />加载 Offer 中…</div>;

  const salLabel = { annual: "年薪", monthly: "月薪", hourly: "时薪" }[data.salaryType] || "薪资";
  const allChecked = (data.docs || []).every((_, i) => checked[i]);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="offer-sign-page">
      <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-2">
        <FileSignature className="h-5 w-5 text-red-500" />
        <div><p className="font-bold">ALIGCUS 阿里建筑 · Offer 与入职文件签署</p><p className="text-[11px] text-slate-300">Offer Letter & Onboarding Documents E-Sign</p></div>
      </div>
      <div className="max-w-[680px] mx-auto p-4">
        {done ? (
          <div className="bg-white rounded-2xl border border-green-200 p-8 text-center my-6" data-testid="offer-sign-done">
            <ShieldCheck className="h-14 w-14 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-slate-800">签署完成，欢迎加入 ALIGCUS！</p>
            <p className="text-sm text-slate-500 mt-1">您的签名已归档，HR 将与您确认入职安排。{data.signedAt ? ` 签署时间 ${data.signedAt.slice(0, 19).replace("T", " ")}` : ""}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 my-4" data-testid="offer-sign-form">
            <p className="text-sm text-slate-600 mb-3">尊敬的 <span className="font-semibold text-slate-800">{data.name}</span>，请确认以下录用条款与入职文件并签名。</p>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-4 text-[13px] leading-relaxed text-slate-700 space-y-1.5" data-testid="offer-sign-summary">
              <p><span className="font-semibold">职位 Position：</span><span className="text-red-600 font-semibold">{data.jobTitle}</span></p>
              <p><span className="font-semibold">工作地点 Location：</span>{data.branch}</p>
              <p><span className="font-semibold">入职日期 Start Date：</span>{fmtD(data.hireDate)}</p>
              <p><span className="font-semibold">{salLabel} Compensation：</span><span className="text-red-600 font-semibold">${Math.round(data.salary || 0).toLocaleString()}</span></p>
              {data.note && <p><span className="font-semibold">备注：</span>{data.note}</p>}
            </div>
            <div className="mt-4 space-y-2" data-testid="offer-sign-docs">
              <p className="text-[13px] font-semibold text-slate-700">入职文件签收确认（请逐项勾选）</p>
              {(data.docs || []).map((d, i) => (
                <label key={d.id || i} className="flex items-start gap-2 text-[13px] text-slate-600 cursor-pointer">
                  <input data-testid={`offer-sign-doc-${i}`} type="checkbox" checked={!!checked[i]} onChange={(e) => setChecked((s) => ({ ...s, [i]: e.target.checked }))} className="mt-0.5" />
                  <span>{d.title || d}
                    {d.hasFile && <a data-testid={`offer-sign-doc-view-${i}`} href={`${BACKEND}/api/esign/offer/${token}/doc/${d.id}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-2 text-[12px] text-indigo-600 underline">查看文件</a>}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <p className="text-[13px] font-semibold text-slate-700 mb-1">签署人姓名 Full Legal Name</p>
              <input data-testid="offer-sign-name" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="mt-4">
              <p className="text-[13px] font-semibold text-slate-700 mb-1">手写签名 Signature</p>
              <SignaturePad onChange={setSig} />
            </div>
            {subErr && <p data-testid="offer-sign-error" className="mt-3 text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{subErr}</p>}
            <button onClick={submit} disabled={!allChecked || !sig || !name.trim() || submitting} data-testid="offer-sign-submit"
              className="mt-4 w-full h-12 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white font-semibold flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}确认签署 Confirm & Sign
            </button>
            <p className="text-[11px] text-slate-400 mt-2 text-center">签署后签名将自动嵌入 Offer 信函 PDF 并归档至您的员工档案。</p>
          </div>
        )}
      </div>
    </div>
  );
}
